<template>
  <section class="company-list">
    <div class="company-list__container">
      <header>
        <div class="sort-wrapper">
          <div class="search-wrapper">
            <div class="search">
              <SearchIcon class="search-icon" @click="onSearch" />
              <input
                type="text"
                placeholder="Search for a company"
                v-model="searchValue"
                @keyup.enter.prevent="onSearch"
                @keydown.enter.prevent
              />
            </div>
            <!-- <ToggleSwitch
              v-model="showOnlyExpired"
              text="Only Expired Companies"
            /> -->
          </div>
          <div class="actions">
            <!-- default sort should be Renewal Date: company with the closest renewal date should be higher up-->
            <div class="sort">
              <span>Sort by </span>
              <div class="sort-container">
                <span class="selected" @click="sortOpen = !sortOpen">{{
                  selectedSortOption.text
                }}</span>
                <div class="sort-dropdown" v-if="sortOpen">
                  <span
                    v-for="option in sortOptions"
                    :key="option.value"
                    @click="sortOptionClicked(option)"
                    class="sort-item"
                    :class="{
                      selected: option.value === selectedSortOption.value,
                    }"
                    >{{ option.text }}</span
                  >
                  <!-- <span class="sort-item">Plants</span> -->
                </div>
              </div>
            </div>
            <div class="sort-dir" @click="sortDirAsc = !sortDirAsc">
              <SortIcon />
            </div>
            <div class="filter-toggle" @click="filterShow = !filterShow">
              <FilterShowIcon v-if="!filterShow" />
              <FilterCloseIcon v-else />
            </div>
            <span class="clearFilters" v-if="filterActive" @click="clearFilters"
              >Clear Filters</span
            >
          </div>
        </div>
        <div class="company-list__filters" v-if="filterShow">
          <div class="form-row">
            <div class="filter-item">
              <label>Renewal Date</label>
              <div class="flex">
                <DatePicker
                  :key="datePickersStateKey"
                  v-model="filters.startRenewalDate"
                  :max="filters.endRenewalDate ?? maxDate"
                  @change="onFilterDateChange"
                />
                -
                <DatePicker
                  :key="datePickersStateKey"
                  v-model="filters.endRenewalDate"
                  :min="filters.startRenewalDate ?? minDate"
                  @change="onFilterDateChange"
                />
              </div>
            </div>
            <div class="filter-item">
              <label>Last Interaction</label>
              <div class="flex">
                <DatePicker
                  :key="datePickersStateKey"
                  v-model="filters.startLastContactDate"
                  :max="filters.endLastContactDate ?? new Date()"
                  @change="onFilterDateChange"
                />
                -
                <DatePicker
                  :key="datePickersStateKey"
                  v-model="filters.endLastContactDate"
                  :min="filters.startLastContactDate ?? minDate"
                  :max="new Date()"
                  @change="onFilterDateChange"
                />
              </div>
            </div>
          </div>
        </div>
      </header>
      <main @scroll="scrollHandler">
        <div class="companies-list">
          <div
            class="company-list__item"
            v-for="company in companies"
            :key="company.CompanyId"
            @click="showCompanyDetails(company)"
          >
            <header>
              <div
                v-show="
                  company.RenewalInterval &&
                  company.RenewalInterval <= 4 &&
                  company.RenewalInterval > 0
                "
                class="status"
                :class="'level-' + company.RenewalInterval"
              ></div>
              <div class="name">
                {{ company.CompanyName }} / {{ company.CompanyId }}
              </div>
              <span class="category">{{ company.CompanyCategory }}</span>
              <span class="value">{{
                CurrencyFormatterRounded.format(company.CertificationFee)
              }}</span>
              <span v-if="company.NumberOfPlants" class="plants"
                >{{ company.NumberOfPlants }} Plant{{
                  company.NumberOfPlants > 1 ? "s" : ""
                }}</span
              >
              <span v-if="company.HasBillHold" class="bill-hold"
                >Bill Hold</span
              >
              <span v-if="company.HasLocHold" class="loc-hold">LOC Hold</span>
            </header>
            <div class="meta-info">
              <div :class="{ expired: company.IsCertificationExpired }">
                Renewal on
                <span>{{
                  moment(company.CompanyExpirationDate).format("MM/DD/YYYY")
                }}</span>
              </div>

              <div v-if="company.LastInteractionDate">
                | Last Interaction
                <span>{{
                  moment(company.LastInteractionDate).format("MM/DD/YYYY")
                }}</span>
              </div>
              |
              <div>
                Primary Contact
                <span
                  >{{ company.ContactLastName }},
                  {{ company.ContactFirstName }}</span
                >
              </div>
            </div>
          </div>
        </div>
        <Loader
          v-show="loading"
          class="companies-loader"
          size="large"
          type="pulsing"
          data-cy="companies-loader"
        />
      </main>
    </div>
    <CompanyDetails
      v-if="companyPanelShow"
      :company="detailsCompany!"
      @close="companyPanelShow = false"
    />
  </section>
</template>

<script setup lang="ts">
import { onBeforeMount, ref, watch } from "vue";
import SearchIcon from "@/icons/CrmSearchIcon.vue";
import SortIcon from "@/icons/SortIcon.vue";
import FilterShowIcon from "@/icons/FilterShowIcon.vue";
import FilterCloseIcon from "@/icons/FilterCloseIcon.vue";
import CompanyDetails from "./CompanyDetails.vue";
import { DatePicker } from "@progress/kendo-vue-dateinputs";
import moment from "moment";
import type { MiniCrmCompany } from "@/Domain/MiniCrmCompany";
import { handleResponsePromiseAsync } from "@/shared/helpers/apiHelper";
import { api } from "@/api";
import { useMainStore } from "@/stores/useMainStore";
import { EnumFilterStatementConnector } from "@/Domain/EnumFilterStatementConnector";
import type { FilterExpression } from "@/Domain/FilterExpression";
import type { FilterGroup } from "@/Domain/FilterGroup";
import { Loader } from "@progress/kendo-vue-indicators";
import { CurrencyFormatterRounded } from "@/shared/helpers/formatHelper";
import ToggleSwitch from "@/shared/ui/switch/ToggleSwitch.vue";
import { useMiniCrmStore } from "@/stores/useMiniCrmStore";

interface CompanyFilters {
  startRenewalDate: Date | undefined;
  endRenewalDate: Date | undefined;
  startLastContactDate: Date | undefined;
  endLastContactDate: Date | undefined;
}

const main = useMainStore();
const crmStore = useMiniCrmStore();

const minDate = new Date(1900, 0, 1);
const maxDate = moment().add(10, "years").toDate();

const props = defineProps<{
  engagementInterval?: number;
  renewalInterval?: number;
}>();

const filterShow = ref(false);
const companyPanelShow = ref(false);
const searchValue = ref("");
const sortDirAsc = ref(true);
const sortOpen = ref(false);
const filterActive = ref(false);
const take = ref(50);
const skip = ref(0);
const totalCompanies = ref(0);
const loading = ref(false);
const datePickersStateKey = ref(0);
const showOnlyExpired = ref(false);

const filters = ref<CompanyFilters>({
  startRenewalDate: undefined,
  endRenewalDate: undefined,
  startLastContactDate: undefined,
  endLastContactDate: undefined,
});

const companies = ref<MiniCrmCompany[]>([]);
const detailsCompany = ref<MiniCrmCompany | null>(null);

const sortOptions = [
  { value: "qCertificationEnds", text: "Renewal Date" },
  { value: "QLastCrmInteraction", text: "Last Interaction Date" },
  { value: "Name", text: "Company Name" },
  { value: "QCertificationFee", text: "Certification Fee" },
];

const selectedSortOption = ref(sortOptions[0]);

const buildJsonFilterString = function () {
  if (
    !filters.value.startRenewalDate &&
    !filters.value.endRenewalDate &&
    !filters.value.startLastContactDate &&
    !filters.value.endLastContactDate &&
    !showOnlyExpired.value
  )
    return "";

  const filterModel = {
    FilterGroups: [],
    BooleanLogicOperator: EnumFilterStatementConnector.And,
  } as FilterExpression;

  if (filters.value.startRenewalDate || filters.value.endRenewalDate) {
    const renewalGroup = {
      FilterFields: [],
      BooleanLogicOperator: EnumFilterStatementConnector.And,
      FieldName: "QCertificationEnds",
      InternalFieldName: "",
    } as FilterGroup;

    if (filters.value.startRenewalDate) {
      renewalGroup.FilterFields.push({
        Operator: "gte",
        Value: moment(filters.value.startRenewalDate).format("YYYY-MM-DD"),
      });
    }

    if (filters.value.endRenewalDate) {
      renewalGroup.FilterFields.push({
        Operator: "lte",
        Value: moment(filters.value.endRenewalDate).format("YYYY-MM-DD"),
      });
    }

    filterModel.FilterGroups.push(renewalGroup);
  }

  if (filters.value.startLastContactDate || filters.value.endLastContactDate) {
    const lastContactGroup = {
      FilterFields: [],
      BooleanLogicOperator: EnumFilterStatementConnector.And,
      FieldName: "QLastCrmInteraction",
      InternalFieldName: "",
    } as FilterGroup;

    if (filters.value.startLastContactDate) {
      lastContactGroup.FilterFields.push({
        Operator: "gte",
        Value: moment(filters.value.startLastContactDate).format("YYYY-MM-DD"),
      });
    }

    if (filters.value.endLastContactDate) {
      lastContactGroup.FilterFields.push({
        Operator: "lte",
        Value: moment(filters.value.endLastContactDate).format("YYYY-MM-DD"),
      });
    }

    filterModel.FilterGroups.push(lastContactGroup);
  }

  if (showOnlyExpired.value) {
    const expiredGroup = {
      FilterFields: [],
      BooleanLogicOperator: EnumFilterStatementConnector.And,
      FieldName: "IsCertificationExpired",
      InternalFieldName: "",
    } as FilterGroup;

    expiredGroup.FilterFields.push({
      Operator: "eq",
      Value: "true",
    });

    filterModel.FilterGroups.push(expiredGroup);
  }

  return JSON.stringify(filterModel);
};

const getCompaniesAsync = async function () {
  if (loading.value) return;
  loading.value = true;

  if (skip.value == 0) {
    companies.value = [];
    totalCompanies.value = 0;
    companyPanelShow.value = false;
  }

  const getCompaniesPromise = api.get("GetRcCompanyListWithFiltering", {
    userLogin: main.userLogin,
    emulatedPersonJobId: crmStore.emulatedPersonJobId,
    engagementInterval: props.engagementInterval ?? -1,
    renewalInterval: props.renewalInterval ?? -1,
    searchValue: searchValue.value,
    filters: buildJsonFilterString(),
    skip: skip.value,
    take: take.value,
    valuesForFieldOnly: "",
    sortField: selectedSortOption.value.value,
    ascending: sortDirAsc.value,
    ignoreYear: true,
  });

  await handleResponsePromiseAsync(
    getCompaniesPromise,
    (response) => {
      if (skip.value > 0) companies.value.push(...response.ListItems);
      else companies.value = response.ListItems;
      totalCompanies.value = response.TotalItemsInList;
    },
    "CompanyListView.vue",
    "getCompaniesAsync"
  );

  loading.value = false;
};

const showCompanyDetails = function (company: MiniCrmCompany) {
  detailsCompany.value = company;
  companyPanelShow.value = true;
};

const onSearch = function () {
  skip.value = 0;
  getCompaniesAsync();
};

const sortOptionClicked = function (option: (typeof sortOptions)[0]) {
  selectedSortOption.value = option;
  sortOpen.value = false;
};

const onFilterDateChange = function () {
  filterActive.value = true;
  skip.value = 0;
  getCompaniesAsync();
};

const clearFilters = function () {
  filters.value = {
    startRenewalDate: undefined,
    endRenewalDate: undefined,
    startLastContactDate: undefined,
    endLastContactDate: undefined,
  };
  filterActive.value = false;
  skip.value = 0;

  // Force the datepickers to re-render
  datePickersStateKey.value++;
  getCompaniesAsync();
};

const loadScrollThreshold = 100;

const scrollHandler = function (e: Event) {
  const target = e.target as HTMLDivElement | null;
  if (!target) return;
  if (
    target.scrollTop + loadScrollThreshold <
    target.scrollHeight - target.clientHeight
  )
    return;
  if (skip.value + take.value >= totalCompanies.value) return;
  if (loading.value) return;

  skip.value = skip.value + take.value;
  getCompaniesAsync();
};

watch(
  () => [
    props.engagementInterval,
    props.renewalInterval,
    sortDirAsc.value,
    selectedSortOption.value,
    showOnlyExpired.value,
    crmStore.emulatedPersonJobId,
  ],
  () => {
    skip.value = 0;
    getCompaniesAsync();
  }
);

onBeforeMount(() => {
  getCompaniesAsync();
});
</script>

<style lang="scss" scoped>
.company-list {
  display: flex;
  height: calc(100vh - 235px);

  &__container {
    flex: 1;

    > header {
      box-shadow: 1px 0px 5px rgba(0, 0, 0, 0.2);
      z-index: 10;
      position: relative;
    }

    .sort-wrapper {
      padding: 12px 24px;
      display: flex;
      justify-content: space-between;
      align-items: center;
      background: #fbfbfb;
      box-shadow: 0px 2px 6px -3px rgba(0, 0, 0, 0.08);
      flex-wrap: wrap;
      gap: 12px;
      justify-content: space-around;
      width: 100%;

      .search-wrapper {
        display: flex;
        align-items: center;
        flex: 1;
      }

      .search {
        background: white;
        flex: 1;
        border: 1px solid #eeeff0;
        border-radius: 4px;
        display: flex;
        padding: 3px 8px 2px;
        align-items: center;
        margin-right: 30px;
        max-width: 500px;

        .icon {
          margin-right: 5px;
          font-size: 0;
        }

        input {
          flex: 1;
          border: none;
          margin: 0;
        }
      }

      .search-icon {
        cursor: pointer;
      }
    }

    .actions {
      display: flex;
      gap: 10px;
    }

    .sort {
      min-width: 170px;
      display: flex;
      color: #4e535a;
      gap: 5px;
    }

    .sort-container {
      position: relative;
      cursor: pointer;

      .selected {
        color: #21456e;

        &:after {
          content: "";
          border: 5px solid transparent;
          border-top-color: #21456e;
          position: relative;
          top: 10px;
          margin-left: 5px;
        }
      }

      .sort-dropdown {
        position: absolute;
        box-shadow: 0px 1px 2px 0px rgba(16, 24, 40, 0.06),
          0px 2px 3px 0px rgba(16, 24, 40, 0.1);
        display: flex;
        flex-direction: column;
        background: white;
        border-radius: 2px;
        width: 170px;
        left: 0;
      }

      .sort-item {
        padding: 16px;
        background: white;

        &:nth-child(even) {
          background: #fbfbfb;
        }

        &.selected {
          background: #21456e;
          color: white;
        }
      }
    }

    .sort-dir {
      cursor: pointer;
    }

    .filter-toggle {
      cursor: pointer;
    }

    .clearFilters {
      text-decoration: underline;
      cursor: pointer;
    }
  }

  main {
    overflow: auto;
    height: calc(100% - 65px);
    position: relative;
  }

  &__filters {
    background: #fbfbfb;
    border-top: 1px solid #eeeff0;
    padding: 12px 24px;

    > .form-row {
      display: flex;
      gap: 8px;
    }

    .flex {
      display: flex;
      align-items: center;
      gap: 3px;
    }

    label {
      color: #4e535a;
      margin: 0;
      padding: 0;
      text-align: left;
      min-width: none;
    }

    .filter-item {
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      justify-content: flex-start;
    }
    .filter-item:not(:last-child) {
      padding-right: 8px;
      border-right: 1px solid #eeeff0;
    }
  }

  &__item {
    background: white;
    padding: 16px 24px;
    cursor: pointer;

    > header {
      display: flex;
      align-items: center;
      gap: 4px;
      margin-bottom: 10px;
    }

    > .meta-info {
      display: flex;
      align-items: center;
      gap: 4px;
      color: #68707a;
      margin-left: 10px;

      .expired {
        color: #ab1400;
      }
    }

    .status {
      border-radius: 50%;
      height: 6px;
      width: 6px;
      background: #999;

      &.level-1 {
        background: #ab1400;
      }

      &.level-2 {
        background: #fd6150;
      }

      &.level-3 {
        //background: #ff9d42;
        background: #fecc4b;
      }

      &.level-4 {
        //background: #fecc4b;
        background: #008b38;
      }

      // &.level-5 {
      //   background: #008b38;
      // }
    }

    .name {
      font-size: 14px;
      font-weight: bold;
      color: #1f262e;
      margin-right: 10px;
    }

    .category {
      border-radius: 20px;
      background: #275182;
      color: white;
      padding: 5px 7px;
      line-height: 1;
    }

    .value {
      border-radius: 20px;
      background: #325ea3;
      color: white;
      padding: 5px 7px;
      line-height: 1;
    }

    .bill-hold {
      border-radius: 20px;
      background: #ff9d42;
      color: white;
      padding: 5px 7px;
      line-height: 1;
    }

    .loc-hold {
      border-radius: 20px;
      background: #fecc4b;
      color: white;
      padding: 5px 7px;
      line-height: 1;
    }

    .plants {
      border-radius: 20px;
      background: #e3ecf7;
      color: #325ea3;
      padding: 5px 7px;
      line-height: 1;
      border: 1px solid #e3ecf7;
    }

    &.selected {
      background: rgba(50, 94, 163, 0.12);

      .plants {
        border-color: #325ea3;
      }
    }

    &:nth-child(even) {
      background: #fbfbfb;
    }
  }

  .companies-loader {
    display: flex;
    justify-content: center;
    margin: 32px 0;
  }
}
</style>
